import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { assessmentApi, oesApi, adminApi, postureRequestApi, adminCriticalityApi, settingsApi } from '../../services/api';
import CustomSelect from '../../components/common/CustomSelect';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import AssessmentGapModal from '../../components/admin/AssessmentGapModal';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

const CRITICALITY_ORDER = { mb: 1, b: 2, m: 3, a: 4, ma: 5 };
function meetsCiiThreshold(oesCriticality, threshold) {
  if (!oesCriticality || !threshold) return false;
  const c = (oesCriticality + '').toLowerCase();
  const t = (threshold + '').toLowerCase();
  return (CRITICALITY_ORDER[c] ?? 0) >= (CRITICALITY_ORDER[t] ?? 0);
}

export default function AssessmentDetail() {
  const labels = useLabels();
  const t = useT();
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [oesList, setOesList] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showAssignOesModal, setShowAssignOesModal] = useState(false);
  const [availableOes, setAvailableOes] = useState([]);
  const [selectedOesId, setSelectedOesId] = useState('');
  const [assigningOes, setAssigningOes] = useState(false);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState(null);
  const [confirmRemoveOesOpen, setConfirmRemoveOesOpen] = useState(false);
  const [oesToRemove, setOesToRemove] = useState(null);
  const [allAssessments, setAllAssessments] = useState([]);
  const [showGapModal, setShowGapModal] = useState(false);
  const [postureRequests, setPostureRequests] = useState([]);
  const [completedCriticalityList, setCompletedCriticalityList] = useState([]);
  const [completedPostureList, setCompletedPostureList] = useState([]);
  const [assessmentSettings, setAssessmentSettings] = useState({});
  const [requestingPostureKey, setRequestingPostureKey] = useState(null);
  const [sectorLists, setSectorLists] = useState([]);

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';
  const isOrgAdmin = role === 'admin';
  const isAssessmentAdmin = role === 'assessment_admin';
  const canManageAssessment = isGlobalAdmin || isOrgAdmin || isAssessmentAdmin;

  // Helper function to get sector name from IDs
  const getSectorName = (sectorListId, sectorId) => {
    if (!sectorListId || !sectorId) return t('assessment.unassigned');
    const list = sectorLists.find(sl => sl.id === sectorListId);
    if (!list || !list.sectors) return 'Unknown';
    const sector = list.sectors.find(s => s.id === sectorId);
    return sector ? sector.name : 'Unknown';
  };

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  useEffect(() => {
    fetchAssessmentData();
    fetchAllAssessments();
    // Load sector lists
    adminApi.getSectorLists()
      .then((r) => setSectorLists(r.data || []))
      .catch(() => setSectorLists([]));
  }, [assessmentId]);

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);
      const [assessmentRes, adminsRes, oesRes] = await Promise.all([
        assessmentApi.getById(assessmentId),
        assessmentApi.getAdmins(assessmentId),
        oesApi.getAll(0, 100, null, null, parseInt(assessmentId))
      ]);

      setAssessment(assessmentRes.data);
      setAdmins(adminsRes.data);
      setOesList(oesRes.data);

      const orgId = assessmentRes.data?.organization_id;
      const aid = parseInt(assessmentId, 10);
      const [prRes, critRes, postureRes, settingsRes] = await Promise.all([
        postureRequestApi.list(aid),
        adminCriticalityApi.getCompletedAssessments(),
        adminCriticalityApi.getCompletedPostureAssessments(),
        orgId != null ? settingsApi.get('organization', orgId) : Promise.resolve({ data: {} }),
      ]);
      setPostureRequests(prRes.data || []);
      setCompletedCriticalityList(critRes.data || []);
      setCompletedPostureList(postureRes.data || []);
      setAssessmentSettings(settingsRes.data || {});
    } catch (error) {
      console.error('Error fetching assessment data:', error);
      showToast('error', t('assessment.failedLoadDetails'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getAllUsers();
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAllAssessments = async () => {
    try {
      const response = await assessmentApi.getAll();
      const assessments = response.data || [];
      setAllAssessments(assessments);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };

  const fetchAvailableOes = async () => {
    try {
      const response = await oesApi.getAll(0, 1000, null, null, null);
      const list = response.data || [];
      const filtered = list.filter((oes) => String(oes.assessment_id) !== String(assessmentId));
      setAvailableOes(filtered);
    } catch (error) {
      console.error('Error fetching available OES:', error);
      setAvailableOes([]);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    try {
      await assessmentApi.assignAdmin(assessmentId, selectedUserId);
      setShowAddAdminModal(false);
      setSelectedUserId('');
      fetchAssessmentData();
      showToast('success', t('assessment.adminAdded'));
    } catch (error) {
      console.error('Error adding admin:', error);
      showToast('error', t('assessment.failedAddAdmin'));
    }
  };

  const handleRemoveAdmin = (userId) => {
    const admin = admins.find(a => a.id === userId);
    setAdminToRemove(admin);
    setConfirmOpen(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!adminToRemove) return;
    try {
      await assessmentApi.removeAdmin(assessmentId, adminToRemove.id);
      fetchAssessmentData();
      showToast('success', t('assessment.adminRemoved'));
    } catch (error) {
      console.error('Error removing admin:', error);
      showToast('error', t('assessment.failedRemoveAdmin'));
    } finally {
      setConfirmOpen(false);
      setAdminToRemove(null);
    }
  };

  const handleAssignOes = async (e) => {
    e.preventDefault();
    if (!selectedOesId) return;
    try {
      setAssigningOes(true);
      await adminApi.updateOES(selectedOesId, { assessment_id: parseInt(assessmentId, 10) });
      setShowAssignOesModal(false);
      setSelectedOesId('');
      fetchAssessmentData();
      showToast('success', t('assessment.entityAssigned', { entity: labels.entityName }));
    } catch (error) {
      console.error('Error assigning OES:', error);
      showToast('error', t('assessment.failedAssignEntity'));
    } finally {
      setAssigningOes(false);
    }
  };

  const handleRemoveOes = (oes) => {
    setOesToRemove(oes);
    setConfirmRemoveOesOpen(true);
  };

  const confirmRemoveOes = async () => {
    if (!oesToRemove) return;
    
    try {
      await oesApi.removeFromAssessment(oesToRemove.id);
      fetchAssessmentData();
      showToast('success', t('assessment.entityRemoved', { entity: labels.entityName }));
    } catch (error) {
      console.error('Error removing OES:', error);
      const errorMessage = error.response?.data?.detail || t('assessment.failedRemoveEntity');
      showToast('error', errorMessage);
    } finally {
      setConfirmRemoveOesOpen(false);
      setOesToRemove(null);
    }
  };

  const getAssessmentName = (assessmentId) => {
    if (!assessmentId) return t('assessment.unassigned');
    const assessment = allAssessments.find(a => a.id === assessmentId);
    return assessment ? assessment.name : `Assessment #${assessmentId}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-700';
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-eucnet-grey-200 text-eucnet-grey-600';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await assessmentApi.updateStatus(assessmentId, newStatus);
      fetchAssessmentData();
      showToast('success', t('assessment.statusUpdated', { status: newStatus }));
    } catch (error) {
      console.error('Error updating assessment status:', error);
      const errorMessage = error.response?.data?.detail || t('assessment.failedUpdateStatus');
      showToast('error', errorMessage);
    }
  };

  const getStatusButton = () => {
    if (!canManageAssessment) return null;
    
    const currentStatus = assessment.status;
    const hasOes = oesList.length > 0;
    
    if (currentStatus === 'draft') {
      return (
        <div className="ml-4 flex flex-col items-end">
          <button
            onClick={() => handleStatusUpdate('active')}
            disabled={!hasOes}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${
              hasOes
                ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={!hasOes ? t('assessment.addEntityBeforeStart', { entity: labels.entityName }) : t('assessment.startAssessmentTooltip')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('assessment.startAssessment')}
          </button>
        </div>
      );
    }
    
    if (currentStatus === 'active') {
      return (
        <div className="ml-4 flex gap-2">
          <button
            onClick={() => handleStatusUpdate('paused')}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('assessment.pause')}
          </button>
          <button
            onClick={() => handleStatusUpdate('completed')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('assessment.endAssessment')}
          </button>
        </div>
      );
    }
    
    if (currentStatus === 'paused') {
      return (
        <button
          onClick={() => handleStatusUpdate('active')}
          className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('assessment.resume')}
        </button>
      );
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || !assessment) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      
      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/assessments')}
          className="mb-4 text-eucnet-blue hover:text-eucnet-navy flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          {t('assessment.backToAssessments')}
        </button>

        {/* Assessment Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-eucnet-navy">{assessment.name}</h1>
              <p className="mt-2 text-eucnet-grey-600">{assessment.description}</p>
            </div>
            <div className="flex items-center">
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(assessment.status)}`}>
                {assessment.status}
              </span>
              {getStatusButton()}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t border-eucnet-grey-200">
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.startDate')}</div>
              <div className="text-lg font-semibold text-eucnet-navy">{formatDate(assessment.start_date)}</div>
            </div>
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.endDate')}</div>
              <div className="text-lg font-semibold text-eucnet-navy">{formatDate(assessment.end_date)}</div>
            </div>
          </div>
        </div>

        {/* Assessment Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-eucnet-navy mb-4">{t('assessment.assessmentConfig')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.criticalityGuideline')}</div>
              <div className="text-base font-semibold text-eucnet-navy">
                {assessment.criticality_guideline || t('assessment.notSpecified')}
              </div>
            </div>
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.controlFramework')}</div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-eucnet-navy">
                  {assessment.posture_framework || t('assessment.notSpecified')}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.estimatedDuration')}</div>
              <div className="text-base font-semibold text-eucnet-navy">
                {assessment.estimated_duration_weeks || t('assessment.notSpecified')}
              </div>
            </div>
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('assessment.estimatedHours')}</div>
              <div className="text-base font-semibold text-eucnet-navy">
                {assessment.estimated_hours || t('assessment.notSpecified')}
              </div>
            </div>
          </div>
          {assessment.notes && (
            <div className="mt-6 pt-4 border-t border-eucnet-grey-200">
              <div className="text-sm text-eucnet-grey-600 mb-2">{t('assessment.notes')}</div>
              <div className="text-sm text-eucnet-grey-700 whitespace-pre-wrap">
                {assessment.notes}
              </div>
            </div>
          )}
        </div>

        {/* Assessment Admins Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.assessmentAdministrators')}</h2>
            {(isGlobalAdmin || isOrgAdmin) && (
              <button
                onClick={() => {
                  fetchUsers();
                  setShowAddAdminModal(true);
                }}
                className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t('assessment.addAdmin')}
              </button>
            )}
          </div>

          {admins.length === 0 ? (
            <p className="text-eucnet-grey-600 text-center py-8">{t('assessment.noAdminsAssigned')}</p>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-4 bg-eucnet-grey-50 rounded-lg">
                  <div>
                    <div className="font-medium text-eucnet-navy">{admin.username}</div>
                    <div className="text-sm text-eucnet-grey-600">{admin.email}</div>
                  </div>
                  {(isGlobalAdmin || isOrgAdmin) && (
                    <button
                      onClick={() => handleRemoveAdmin(admin.id)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title={t('assessment.removeFromAssessment')}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* OES List Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.organizations', { entity: labels.entityName })}</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setShowAssignOesModal(true);
                  setSelectedOesId('');
                  fetchAvailableOes();
                }}
                className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t('assessment.addEntity', { entity: labels.entityName })}
              </button>
              {(isGlobalAdmin || isOrgAdmin) && (
                <Link
                  to={`/oes?assessment_id=${assessmentId}`}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-eucnet-blue border border-eucnet-blue hover:bg-eucnet-blue hover:text-white transition-colors"
                >
                  {t('assessment.openEntityManagement', { entity: labels.entityName })}
                </Link>
              )}
            </div>
          </div>

          {oesList.length === 0 ? (
            <p className="text-eucnet-grey-600 text-center py-8">{t('assessment.noOrganizationsAdded')}</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {oesList.map((oes) => (
                <div key={oes.id} className="p-4 border border-eucnet-grey-200 rounded-lg hover:shadow-md transition-shadow relative">
                  <div className="flex items-center gap-3 mb-1">
                    {oes.logo && (
                      <img
                        src={oes.logo.startsWith('data:') ? oes.logo : `data:image/jpeg;base64,${oes.logo}`}
                        alt=""
                        className="h-10 w-10 flex-shrink-0 rounded object-contain bg-gray-50 border border-eucnet-grey-200"
                      />
                    )}
                    <div className="font-medium text-eucnet-navy min-w-0 flex-1">{oes.name}</div>
                  </div>
                  <div className="text-sm text-eucnet-grey-600">{getSectorName(oes.sector_list_id, oes.sector_id)}</div>
                  {canManageAssessment && (
                    <button
                      onClick={() => handleRemoveOes(oes)}
                      className="absolute top-2 right-2 text-red-600 hover:text-red-800 p-1"
                      title={t('assessment.removeFromAssessment')}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assessment requests: control posture requests per OES (status only; results live on Assessment Results page) */}
        {canManageAssessment && oesList.length > 0 && (() => {
          const threshold = assessmentSettings.cii_criticality_threshold || 'M';
          const postureRequestOesIds = new Set((postureRequests || []).map((r) => r.oes_id));
          const completedCriticalityByOes = {};
          (completedCriticalityList || [])
            .filter((c) => Number(c.assessment_id) === Number(assessmentId))
            .forEach((c) => { if (!completedCriticalityByOes[c.oes_id]) completedCriticalityByOes[c.oes_id] = c; });
          const completedPostureByOes = {};
          (completedPostureList || [])
            .filter((p) => Number(p.assessment_id) === Number(assessmentId))
            .forEach((p) => { completedPostureByOes[p.oes_id] = p; });

          const rows = oesList.map((oes) => {
            const crit = completedCriticalityByOes[oes.id];
            const postureReq = postureRequestOesIds.has(oes.id);
            const postureDone = !!completedPostureByOes[oes.id];
            const meetsCii = crit ? meetsCiiThreshold(crit.oes_criticality, threshold) : false;
            const canRequestPosture = !!crit && meetsCii && (!postureReq || postureDone);
            let status;
            if (!crit) status = t('assessment.criticalityNotStarted');
            else if (!meetsCii) status = t('assessment.belowThreshold');
            else if (postureDone) status = t('common.completed');
            else if (postureReq) status = t('assessment.requested');
            else status = t('assessment.notRequested');
            return {
              oes,
              status,
              canRequestPosture,
              postureRequested: postureReq,
              postureCompleted: postureDone,
            };
          });

          return (
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h2 className="text-xl font-bold text-eucnet-navy mb-4">{t('assessment.assessmentRequests')}</h2>
              <p className="text-sm text-eucnet-grey-600 mb-4">
                {t('assessment.assessmentRequestsDesc', { entity: labels.entityNamePlural })}{labels.isCII ? ' (CII)' : ''}
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-eucnet-grey-200">
                  <thead className="bg-eucnet-grey-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-eucnet-grey-600 uppercase">{labels.entityName}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-eucnet-grey-600 uppercase">{t('common.status')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-eucnet-grey-600 uppercase">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-eucnet-grey-200">
                    {rows.map(({ oes, status, canRequestPosture, postureRequested, postureCompleted }) => (
                      <tr key={oes.id} className="hover:bg-eucnet-grey-50/50">
                        <td className="px-4 py-3 text-sm font-medium text-eucnet-navy">{oes.name}</td>
                        <td className="px-4 py-3 text-sm text-eucnet-grey-700">{status}</td>
                        <td className="px-4 py-3 text-right">
                          {canRequestPosture && !postureRequested && (
                            <button
                              type="button"
                              onClick={async () => {
                                setRequestingPostureKey(`${oes.id}:${assessmentId}`);
                                try {
                                  await postureRequestApi.create(oes.id, parseInt(assessmentId, 10));
                                  showToast('success', t('assessment.assessmentRequested'));
                                  fetchAssessmentData();
                                } catch (err) {
                                  showToast('error', err.response?.data?.detail || t('assessment.failedRequestPosture'));
                                } finally {
                                  setRequestingPostureKey(null);
                                }
                              }}
                              disabled={requestingPostureKey !== null}
                              className="px-3 py-1.5 rounded bg-eucnet-sky text-white text-sm font-medium hover:bg-eucnet-navy disabled:opacity-50"
                            >
                              {requestingPostureKey === `${oes.id}:${assessmentId}` ? '...' : t('assessment.requestAssessment')}
                            </button>
                          )}
                          {postureRequested && (
                            <button
                              type="button"
                              onClick={async () => {
                                setRequestingPostureKey(`${oes.id}:${assessmentId}`);
                                try {
                                  await postureRequestApi.delete(oes.id, parseInt(assessmentId, 10));
                                  showToast('success', t('assessment.requestCancelled'));
                                  fetchAssessmentData();
                                } catch (err) {
                                  showToast('error', err.response?.data?.detail || t('assessment.failedCancelRequest'));
                                } finally {
                                  setRequestingPostureKey(null);
                                }
                              }}
                              disabled={requestingPostureKey !== null}
                              className="px-2.5 py-1 rounded border border-amber-400 text-amber-700 text-xs font-medium bg-white hover:bg-amber-50 disabled:opacity-50"
                            >
                              {t('assessment.cancelRequest')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Assign Existing OES Modal */}
      {showAssignOesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-eucnet-grey-200">
              <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.assignExistingEntity', { entity: labels.entityName })}</h2>
            </div>

            <form onSubmit={handleAssignOes} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-eucnet-grey-700 mb-2">
                  {t('assessment.selectEntity', { entity: labels.entityName })} *
                </label>
                <CustomSelect
                  value={selectedOesId}
                  onChange={(e) => setSelectedOesId(e.target.value)}
                  options={[
                    { value: '', label: t('assessment.selectAnEntity', { entity: labels.entityName }) },
                    ...availableOes.map(oes => ({ 
                      value: String(oes.id), 
                      label: `${oes.name} (${getAssessmentName(oes.assessment_id)})` 
                    }))
                  ]}
                  placeholder={t('assessment.selectAnEntity', { entity: labels.entityName })}
                  required
                />
                {availableOes.length === 0 && (
                  <p className="mt-2 text-xs text-eucnet-grey-600">
                    {t('assessment.noExistingEntities', { entity: labels.entityName })}
                  </p>
                )}
                <p className="mt-2 text-xs text-eucnet-grey-600">
                  {t('assessment.assignWillMove', { entity: labels.entityName })}
                </p>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAssignOesModal(false)}
                  className="px-6 py-2 border border-eucnet-grey-300 rounded-lg text-eucnet-grey-700 hover:bg-eucnet-grey-100 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={assigningOes || !selectedOesId}
                  className="px-6 py-2 bg-eucnet-blue hover:bg-eucnet-navy text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {assigningOes ? t('assessment.assigning') : t('assessment.assignEntity', { entity: labels.entityName })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-eucnet-grey-200">
              <h2 className="text-xl font-bold text-eucnet-navy">{t('assessment.addAssessmentAdmin')}</h2>
            </div>

            <form onSubmit={handleAddAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-eucnet-grey-700 mb-2">
                  {t('assessment.selectUser')} *
                </label>
                <CustomSelect
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  options={[
                    { value: '', label: t('assessment.selectAUser') },
                    ...allUsers
                      .filter(u => {
                        // Don't show users already assigned as admins
                        if (admins.find(a => a.id === u.id)) return false;
                        // For Organization Admins, only show Assessment Admins and regular users
                        // (regular users will be converted to Assessment Admins when assigned)
                        if (isOrgAdmin) {
                          return u.role === 'assessment_admin' || u.role === 'user';
                        }
                        // Global Admins can see all users
                        return true;
                      })
                      .map((user) => ({
                        value: String(user.id),
                        label: `${user.username} (${user.email})${user.role === 'user' ? ` - ${t('assessment.willBecomeAssessmentAdmin')}` : ''}`
                      }))
                  ]}
                  placeholder={t('assessment.selectAUser')}
                  required
                />
                <p className="mt-2 text-xs text-eucnet-grey-600">
                  {isOrgAdmin
                    ? t('assessment.orgAdminSelectHint')
                    : t('assessment.globalAdminSelectHint')}
                </p>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddAdminModal(false)}
                  className="px-6 py-2 border border-eucnet-grey-300 rounded-lg text-eucnet-grey-700 hover:bg-eucnet-grey-100 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-eucnet-blue hover:bg-eucnet-navy text-white rounded-lg transition-colors"
                >
                  {t('assessment.addAdmin')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title={t('assessment.removeAssessmentAdmin')}
        message={adminToRemove ? t('assessment.removeAdminConfirm', { name: adminToRemove.username }) : t('assessment.removeAdminConfirmGeneric')}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        onConfirm={confirmRemoveAdmin}
        onCancel={() => {
          setConfirmOpen(false);
          setAdminToRemove(null);
        }}
      />

      <ConfirmModal
        open={confirmRemoveOesOpen}
        title={t('assessment.removeEntityFromAssessment', { entity: labels.entityName })}
        message={oesToRemove ? t('assessment.removeEntityConfirm', { name: oesToRemove.name }) : t('assessment.removeEntityConfirmGeneric', { entity: labels.entityName })}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        onConfirm={confirmRemoveOes}
        onCancel={() => {
          setConfirmRemoveOesOpen(false);
          setOesToRemove(null);
        }}
      />

      <AssessmentGapModal
        isOpen={showGapModal}
        onClose={() => setShowGapModal(false)}
        assessment={assessment}
        onSaved={fetchAssessmentData}
        onNotify={showToast}
      />
    </Layout>
  );
}
